import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

function formatCellValue(value) {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

function escapeCsv(value) {
	if (/[",\n\r]/.test(value)) {
		return `"${value.replace(/"/g, '""')}"`;
	}
	return value;
}

function toSafeFileName(value) {
	const trimmed = value.trim();
	if (!trimmed) return 'report-table-export';
	const safe = trimmed
		.replace(/[\\/:*?"<>|]+/g, '-')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
		.toLowerCase();
	return safe || 'report-table-export';
}

function TableWidget({
	data,
	columnOrder = [],
	searchText = '',
	exportFileName = 'report-table-export',
	onTableMetaChange,
	requestDownloadToken = 0,
}) {
	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState(null);
	const [valueFilters, setValueFilters] = useState({});
	const [openFilterColumn, setOpenFilterColumn] = useState(null);
	const [filterSearch, setFilterSearch] = useState('');
	const [filterPopupPosition, setFilterPopupPosition] = useState(null);

	const rows = useMemo(() => {
		if (Array.isArray(data)) {
			return data.filter((row) => row && typeof row === 'object' && !Array.isArray(row));
		}
		if (data && typeof data === 'object' && Array.isArray(data.rows)) {
			return data.rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row));
		}
		return [];
	}, [data]);

	const columns = useMemo(() => {
		const discoveredColumns = rows.length ? Object.keys(rows[0]) : [];
		if (!discoveredColumns.length) {
			return [];
		}

		const requestedOrder = (Array.isArray(columnOrder) ? columnOrder : [])
			.filter((value) => typeof value === 'string')
			.map((value) => value.trim())
			.filter(Boolean);

		const matched = [];
		const matchedSet = new Set();
		requestedOrder.forEach((name) => {
			if (!discoveredColumns.includes(name) || matchedSet.has(name)) {
				return;
			}
			matched.push(name);
			matchedSet.add(name);
		});

		const remaining = discoveredColumns.filter((name) => !matchedSet.has(name));
		return [...matched, ...remaining];
	}, [rows, columnOrder]);

	const distinctValuesByColumn = useMemo(() => {
		const map = {};
		columns.forEach((column) => {
			const values = new Set();
			rows.forEach((row) => {
				values.add(formatCellValue(row[column]));
			});
			map[column] = Array.from(values).sort((a, b) =>
				a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
			);
		});
		return map;
	}, [rows, columns]);

	const effectiveSearch = (searchText || '').trim().toLowerCase();

	const filteredAndSortedRows = useMemo(() => {
		const filtered = rows.filter((row) => {
			const matchesSearch =
				!effectiveSearch ||
				columns.some((column) => formatCellValue(row[column]).toLowerCase().includes(effectiveSearch));

			if (!matchesSearch) return false;

			return columns.every((column) => {
				const selectedValues = valueFilters[column];
				if (!selectedValues) return true;
				return selectedValues.includes(formatCellValue(row[column]));
			});
		});

		if (!sortColumn || !sortDirection) {
			return filtered;
		}

		const sorted = [...filtered].sort((a, b) => {
			const aVal = formatCellValue(a[sortColumn]);
			const bVal = formatCellValue(b[sortColumn]);

			const aNum = Number(aVal);
			const bNum = Number(bVal);
			const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum) && aVal.trim() !== '' && bVal.trim() !== '';

			if (bothNumeric) {
				return aNum - bNum;
			}

			return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
		});

		return sortDirection === 'asc' ? sorted : sorted.reverse();
	}, [rows, columns, effectiveSearch, sortColumn, sortDirection, valueFilters]);

	useEffect(() => {
		if (!openFilterColumn) return undefined;

		const handleDocumentClick = () => {
			setOpenFilterColumn(null);
			setFilterPopupPosition(null);
		};

		document.addEventListener('click', handleDocumentClick);
		return () => {
			document.removeEventListener('click', handleDocumentClick);
		};
	}, [openFilterColumn]);

	const toggleSort = (column) => {
		if (sortColumn !== column) {
			setSortColumn(column);
			setSortDirection('asc');
			return;
		}

		if (sortDirection === 'asc') {
			setSortDirection('desc');
			return;
		}

		if (sortDirection === 'desc') {
			setSortColumn(null);
			setSortDirection(null);
			return;
		}

		setSortDirection('asc');
	};

	const openFilterPopup = (column, target) => {
		const rect = target.getBoundingClientRect();
		const popupWidth = 240;
		const viewportPadding = 8;
		const maxLeft = window.innerWidth - popupWidth - viewportPadding;
		const left = Math.max(viewportPadding, Math.min(rect.left, maxLeft));

		setOpenFilterColumn(column);
		setFilterSearch('');
		setFilterPopupPosition({
			top: rect.bottom + 6,
			left,
		});
	};

	const toggleFilterValue = (column, value, checked) => {
		const allValues = distinctValuesByColumn[column] || [];
		const current = valueFilters[column] || allValues;
		const nextSet = new Set(current);

		if (checked) {
			nextSet.add(value);
		} else {
			nextSet.delete(value);
		}

		const next = Array.from(nextSet);
		setValueFilters((prev) => {
			if (next.length === allValues.length) {
				const { [column]: removed, ...rest } = prev;
				void removed;
				return rest;
			}
			return { ...prev, [column]: next };
		});
	};

	const isFilterActive = (column) => {
		const allValues = distinctValuesByColumn[column] || [];
		const selectedValues = valueFilters[column];
		if (!selectedValues) return false;
		return selectedValues.length !== allValues.length;
	};

	const sortIndicator = (column) => {
		if (sortColumn !== column || !sortDirection) return null;
		return sortDirection === 'asc' ? '↑' : '↓';
	};

	const downloadTableCsv = useMemo(() => {
		return () => {
		if (!filteredAndSortedRows.length || !columns.length) return;

		const header = columns.map((column) => escapeCsv(column)).join(',');
		const body = filteredAndSortedRows
			.map((row) => columns.map((column) => escapeCsv(formatCellValue(row[column]))).join(','))
			.join('\n');

		const csv = `${header}\n${body}`;
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `${toSafeFileName(exportFileName)}.csv`;
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		URL.revokeObjectURL(url);
		};
	}, [filteredAndSortedRows, columns, exportFileName]);

	useEffect(() => {
		if (typeof onTableMetaChange !== 'function') return;
		onTableMetaChange({
			canDownload: filteredAndSortedRows.length > 0 && columns.length > 0,
			downloadCsv: downloadTableCsv,
		});
	}, [filteredAndSortedRows, columns, onTableMetaChange, downloadTableCsv]);

	useEffect(() => {
		if (!requestDownloadToken) return;
		downloadTableCsv();
		// requestDownloadToken intentionally triggers download by increment from parent.
	}, [requestDownloadToken, downloadTableCsv]);

	if (!rows.length) {
		return (
			<div className="flex h-full items-center justify-center rounded-xl bg-slate-50 px-3 text-[12px] text-slate-500">
				No Rows Returned
			</div>
		);
	}

	return (
		<div className="flex h-full w-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white">
			<div className="min-h-0 flex-1 overflow-auto">
				<table className="w-full min-w-max border-separate border-spacing-0 text-left text-xs text-slate-700">
					<thead>
						<tr>
							{columns.map((column) => {
								const hasFilter = isFilterActive(column);
								const isSortActive = sortColumn === column && Boolean(sortDirection);
								const isFilterOpen = openFilterColumn === column;

								return (
									<th
										key={column}
										className="group relative sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-2 py-1.5 font-semibold text-slate-700"
									>
										<button
											type="button"
											onClick={() => toggleSort(column)}
											className="flex w-full items-center gap-1 truncate pr-10 text-left hover:text-slate-900"
											title={`Sort by ${column}`}
										>
											<span className="truncate">{column}</span>
											{isSortActive ? <span className="text-sky-700">{sortIndicator(column)}</span> : null}
										</button>

										<span
											className={`absolute right-1 top-1/2 inline-flex -translate-y-1/2 items-center rounded bg-slate-50/90 transition-opacity ${
												hasFilter || isFilterOpen || isSortActive
													? 'opacity-100'
													: 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
											}`}
										>
											<button
												type="button"
												onClick={(event) => {
													event.stopPropagation();
													if (isFilterOpen) {
														setOpenFilterColumn(null);
														setFilterPopupPosition(null);
														return;
													}
													openFilterPopup(column, event.currentTarget);
												}}
												className={`inline-flex h-5 w-5 items-center justify-center rounded border text-[10px] ${
													hasFilter || isFilterOpen
														? 'border-sky-300 bg-sky-50 text-sky-700'
														: 'border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-100'
												}`}
												title={`Filter ${column}`}
											>
												<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-3 w-3">
													<path d="M4 6h16l-6.5 7.4V18l-3 1v-5.6L4 6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</button>
										</span>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{filteredAndSortedRows.length ? (
							filteredAndSortedRows.map((row, rowIndex) => (
								<tr key={rowIndex} className="odd:bg-white even:bg-slate-50/50">
									{columns.map((column) => (
										<td key={`${rowIndex}-${column}`} className="border-b border-slate-100 px-2 py-1.5 align-top">
											<span className="whitespace-normal break-words">{formatCellValue(row[column])}</span>
										</td>
									))}
								</tr>
							))
						) : (
							<tr>
								<td colSpan={columns.length} className="px-2 py-6 text-center text-[11px] text-slate-500">
									No matching rows
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{openFilterColumn && filterPopupPosition && typeof document !== 'undefined'
				? createPortal(
						<div
							onClick={(event) => event.stopPropagation()}
							className="fixed z-[400000] w-60 rounded-md border border-slate-200 bg-white p-2 shadow-xl"
							style={{ top: filterPopupPosition.top, left: filterPopupPosition.left }}
						>
							<input
								autoFocus
								type="text"
								value={filterSearch}
								onChange={(event) => setFilterSearch(event.target.value)}
								placeholder="Search values..."
								className="h-8 w-full rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 focus:border-slate-500 focus:outline-none"
							/>

							<div className="mt-2 max-h-48 overflow-auto rounded border border-slate-100 p-1">
								{(distinctValuesByColumn[openFilterColumn] || [])
									.filter((value) => value.toLowerCase().includes(filterSearch.trim().toLowerCase()))
									.map((value) => {
										const allValues = distinctValuesByColumn[openFilterColumn] || [];
										const selected = valueFilters[openFilterColumn] || allValues;
										const checked = selected.includes(value);

										return (
											<label
												key={`${openFilterColumn}-${value}`}
												className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-slate-50"
											>
												<input
													type="checkbox"
													checked={checked}
													onChange={(event) =>
														toggleFilterValue(openFilterColumn, value, event.target.checked)
													}
												/>
												<span className="truncate text-slate-700">{value || '(blank)'}</span>
											</label>
										);
									})}
							</div>

							<div className="mt-2 flex items-center justify-between gap-2">
								<button
									type="button"
									onClick={() => {
										const { [openFilterColumn]: removed, ...rest } = valueFilters;
										void removed;
										setValueFilters(rest);
									}}
									className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
								>
									Select all
								</button>
								<button
									type="button"
									onClick={() => {
										setValueFilters((prev) => ({ ...prev, [openFilterColumn]: [] }));
									}}
									className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
								>
									Clear all
								</button>
								<button
									type="button"
									onClick={() => {
										setOpenFilterColumn(null);
										setFilterPopupPosition(null);
									}}
									className="rounded bg-slate-800 px-2 py-1 text-[11px] text-white hover:bg-slate-700"
								>
									Done
								</button>
							</div>
						</div>,
						document.body
				  )
				: null}
		</div>
	);
}

export default TableWidget;
